/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: activities_notes_junction entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the activities_notes_junction table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the activities_notes_junction API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, activity_id, note_id, trig_note_title, trig_note_type, trig_note_is_internal, trig_note_created_by, trig_note_content, trig_activity_name, trig_activity_status_name, trig_activity_type_name, trig_activity_scheduled_date, created_at, updated_at, created_by, trig_note_assigned_to, trig_activity_assigned_users]
 * Searchable fields: []
 * Filterable fields: [id, activity_id, note_id]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  ActivitiesNotesJunctionRow,
  ActivitiesNotesJunctionInsert,
  ActivitiesNotesJunctionUpdate
} from '../generated_schemas/generated-activities_notes_junction-schema.js';
import { ActivitiesNotesJunctionQueryOptions } from '../generated_schemas/generated-activities_notes_junction-schema.js';
export type { ActivitiesNotesJunctionQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface ActivitiesNotesJunctionClientResponse {
  data: ActivitiesNotesJunctionRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for activities_notes_junction table operations
 *
 * @description Provides type-safe access to activities_notes_junction API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class ActivitiesNotesJunctionClient {
  private readonly basePath = '/activities-notes-junction';

  /**
   * Get all activities_notes_junction records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of activities_notes_junction records
   */
  async getAll(options?: ActivitiesNotesJunctionQueryOptions): Promise<ActivitiesNotesJunctionRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<ActivitiesNotesJunctionClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single activities_notes_junction record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the activities_notes_junction record
   */
  async getById(id: string): Promise<ActivitiesNotesJunctionRow> {
    const response = await httpClient.get<{
      data: ActivitiesNotesJunctionRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new activities_notes_junction record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created activities_notes_junction record
   */
  async create(data: ActivitiesNotesJunctionInsert): Promise<ActivitiesNotesJunctionRow> {
    const response = await httpClient.post<{
      data: ActivitiesNotesJunctionRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing activities_notes_junction record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated activities_notes_junction record
   */
  async update(id: string, data: ActivitiesNotesJunctionUpdate): Promise<ActivitiesNotesJunctionRow> {
    const response = await httpClient.put<{
      data: ActivitiesNotesJunctionRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a activities_notes_junction record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple activities_notes_junction records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created activities_notes_junction records
   */
  async createMany(data: ActivitiesNotesJunctionInsert[]): Promise<ActivitiesNotesJunctionRow[]> {
    const response = await httpClient.post<{
      data: ActivitiesNotesJunctionRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple activities_notes_junction records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search activities_notes_junction with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: ActivitiesNotesJunctionQueryOptions
  ): Promise<ActivitiesNotesJunctionClientResponse> {
    throw new Error('Search operations are not available for activities_notes_junction - no searchable fields configured');
  }

  /**
   * Get count of activities_notes_junction records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a activities_notes_junction record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'activities_notes_junction',
      publicFields: ['id', 'activity_id', 'note_id', 'trig_note_title', 'trig_note_type', 'trig_note_is_internal', 'trig_note_created_by', 'trig_note_content', 'trig_activity_name', 'trig_activity_status_name', 'trig_activity_type_name', 'trig_activity_scheduled_date', 'created_at', 'updated_at', 'created_by', 'trig_note_assigned_to', 'trig_activity_assigned_users'],
      searchableFields: [],
      filterableFields: ['id', 'activity_id', 'note_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'trig_note_title', 'trig_note_type', 'trig_note_is_internal', 'trig_note_created_by', 'trig_note_content', 'trig_activity_name', 'trig_activity_status_name', 'trig_activity_type_name', 'trig_activity_scheduled_date', 'created_at', 'updated_at', 'trig_note_assigned_to', 'trig_activity_assigned_users'],
      requiredFields: ['activity_id', 'note_id'],
    };
  }
}

// Export singleton instance for convenience
export const activitiesNotesJunctionClient = new ActivitiesNotesJunctionClient();
